// src/utils/conflictDetection.ts
import { StaffAllocationGrid } from '../types/grid';
import { Staff } from '../types/data';
import { getTotalAllowanceMinutes } from '../types/staff';

export interface ConflictEntity {
  type: 'teacher' | 'room' | 'class';
  id: string;
  name: string;
  code?: string;
}

export interface ConflictData {
  id: string;
  type: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  lineId: string;
  lineName: string;
  affectedEntities: ConflictEntity[];
  status: 'open' | 'resolved' | 'ignored';
  resolutionOptions: string[];
  details: {
    teacherCode?: string;
    teacherName?: string;
    classIds?: string[];
    classNames?: string[];
    roomId?: string;
    roomName?: string;
  };
}

export interface ConflictSummary {
  totalConflicts: number;
  openConflicts: number;
  criticalConflicts: number;
  conflictsByType: Record<string, number>;
}

/**
 * Detect all conflicts in a staff allocation grid
 */
export function detectConflicts(
  grid: StaffAllocationGrid,
  staff: Staff[],
  activeSemester: 'both' | 'semester1' | 'semester2' = 'both',
  // Allowances are year-scoped (v2.11.0). Without this, a teacher's 2026 and
  // 2027 allowances both subtract from whichever year is being checked, and
  // everyone reads as overloaded.
  year?: number
): ConflictData[] {
  if (!grid || !grid.classes || !grid.lines) {
    return [];
  }

  const conflicts: ConflictData[] = [];
  
  // Filter lines by semester
  const relevantLines = activeSemester === 'both' 
    ? grid.lines 
    : grid.lines.filter(line => line.semester === activeSemester);

  // 1. Detect teacher double booking conflicts
  const teacherConflicts = detectTeacherDoubleBooking(grid, staff, relevantLines);
  conflicts.push(...teacherConflicts);

  // 2. Room conflicts removed - not using rooms in this system
  // const roomConflicts = detectRoomDoubleBooking(grid, relevantLines);
  // conflicts.push(...roomConflicts);

  // 3. Detect teacher load conflicts
  const loadConflicts = detectTeacherLoadConflicts(grid, staff, relevantLines, year);
  conflicts.push(...loadConflicts);

  // 4. Detect unassigned classes - REMOVED: These are not conflicts, just incomplete data
  // const unassignedConflicts = detectUnassignedClasses(grid, relevantLines);
  // conflicts.push(...unassignedConflicts);

  return conflicts;
}

/**
 * Normalise a line-assignment cell into the real teacher(s) it holds. A cell is
 * either a single teacher code ("ASH") or, for multi-teacher cells, a JSON array
 * like [{"teacherCode":"ASH","minutes":220}, ...]. Without this, the raw JSON
 * string was being treated as a single teacher code (garbled descriptions +
 * missed/false conflicts).
 */
function parseAssignedTeachers(
  value: string | null | undefined
): Array<{ teacherCode: string; minutes?: number }> {
  if (!value || typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      return arr
        .map((t: any) =>
          typeof t === 'string'
            ? { teacherCode: t }
            : { teacherCode: t?.teacherCode, minutes: typeof t?.minutes === 'number' ? t.minutes : undefined }
        )
        .filter((t: { teacherCode?: string }) => !!t.teacherCode) as Array<{ teacherCode: string; minutes?: number }>;
    } catch {
      return [{ teacherCode: trimmed }];
    }
  }
  return [{ teacherCode: trimmed }];
}

/**
 * Format a teacher for display as "First Last (CODE)". The Staff record uses
 * `fullName` + `initial`; fall back across a few field names for safety, and to
 * the bare code if no staff match was found.
 */
function formatTeacherLabel(teacher: any, code: string): string {
  if (!teacher) return code;
  const name =
    teacher.fullName ||
    teacher.name ||
    [teacher.firstName, teacher.lastName].filter(Boolean).join(' ').trim();
  const displayCode = teacher.initial || teacher.staffCode || teacher.code || code;
  return name ? `${name} (${displayCode})` : displayCode;
}

/** Resolve a stored teacher code to a Staff record (by initial/staffCode/id, case-insensitively). */
function findTeacher(staff: Staff[], code: string): Staff | undefined {
  if (!code) return undefined;
  const lc = code.toLowerCase();
  return (staff || []).find((s: any) =>
    s.initial === code ||
    s.staffCode === code ||
    s.id === code ||
    (typeof s.initial === 'string' && s.initial.toLowerCase() === lc)
  );
}

/**
 * Detect teacher double booking conflicts
 */
function detectTeacherDoubleBooking(
  grid: StaffAllocationGrid,
  staff: Staff[],
  lines: any[]
): ConflictData[] {
  const conflicts: ConflictData[] = [];
  const teacherAssignments = new Map<string, Map<string, string[]>>();

  // Build teacher assignments by line
  // Filter out composite sub-classes to avoid false conflicts
  // (sub-classes are hidden and their teaching is counted under the composite class)
  grid.classes
    .filter(classItem => !classItem.isCompositeSubClass)
    .forEach(classItem => {
      Object.entries(classItem.lineAssignments || {}).forEach(([lineId, rawValue]) => {
        if (!lines.some(l => l.id === lineId)) return;
        // A cell may hold one teacher code or a JSON array of teachers; record each
        // real teacher once per class-line so multi-teacher cells are handled right.
        const codes = Array.from(new Set(parseAssignedTeachers(rawValue).map(t => t.teacherCode)));
        codes.forEach(teacherCode => {
          if (!teacherAssignments.has(teacherCode)) {
            teacherAssignments.set(teacherCode, new Map());
          }
          const teacherLines = teacherAssignments.get(teacherCode)!;
          if (!teacherLines.has(lineId)) {
            teacherLines.set(lineId, []);
          }
          teacherLines.get(lineId)!.push(classItem.id);
        });
      });
    });

  // Check for conflicts
  teacherAssignments.forEach((lineAssignments, teacherCode) => {
    lineAssignments.forEach((classIds, lineId) => {
      if (classIds.length > 1) {
        const teacher = findTeacher(staff, teacherCode);
        const line = lines.find(l => l.id === lineId);
        const conflictClasses = classIds.map(classId => {
          const classItem = grid.classes.find(c => c.id === classId);
          return classItem;
        }).filter(Boolean);

        const teacherLabel = formatTeacherLabel(teacher, teacherCode);
        const conflict: ConflictData = {
          id: `teacher_double_${teacherCode}_${lineId}`,
          type: 'teacher_double_booking',
          description: `Teacher ${teacherLabel} assigned to ${classIds.length} classes in the same line`,
          severity: 'critical',
          lineId,
          lineName: line?.name || `Line ${lineId}`,
          affectedEntities: [
            {
              type: 'teacher',
              id: teacherCode,
              name: teacherLabel,
              code: teacherCode
            },
            ...conflictClasses.map(cls => ({
              type: 'class' as const,
              id: cls!.id,
              name: cls!.className,
              code: cls!.className
            }))
          ],
          status: 'open',
          resolutionOptions: [
            ...conflictClasses.slice(1).map(cls => `Reassign teacher for ${cls!.className}`),
            'Find alternative teacher for one of the classes'
          ],
          details: {
            teacherCode,
            teacherName: teacherLabel,
            classIds,
            classNames: conflictClasses.map(cls => cls!.className)
          }
        };

        conflicts.push(conflict);
      }
    });
  });

  return conflicts;
}

/**
 * Room conflict detection - REMOVED: Not using rooms in this system
 */
// function detectRoomDoubleBooking(grid: StaffAllocationGrid, lines: any[]): ConflictData[] {
//   // Room conflicts not needed - this system doesn't track room assignments
//   return [];
// }

/**
 * Detect teacher load conflicts
 */
function detectTeacherLoadConflicts(
  grid: StaffAllocationGrid,
  staff: Staff[],
  lines: any[],
  year?: number
): ConflictData[] {
  const conflicts: ConflictData[] = [];
  const teacherMinutes = new Map<string, number>();

  // Calculate teacher loads in minutes (same logic as TeacherLoadPanel)
  // Filter out composite sub-classes to avoid double-counting minutes
  grid.classes
    .filter(classItem => !classItem.isCompositeSubClass)
    .forEach(classItem => {
      Object.entries(classItem.lineAssignments || {}).forEach(([lineId, rawValue]) => {
        if (!lines.some(l => l.id === lineId)) return;
        // Attribute minutes to each real teacher; multi-teacher cells carry
        // per-teacher minutes in the JSON, otherwise use the class's weekly minutes.
        parseAssignedTeachers(rawValue).forEach(({ teacherCode, minutes }) => {
          const classMinutes = typeof minutes === 'number' ? minutes : (classItem.minutesPerWeek || 360);
          teacherMinutes.set(teacherCode, (teacherMinutes.get(teacherCode) || 0) + classMinutes);
        });
      });
    });

  // Check for overloaded teachers based on utilization percentage
  teacherMinutes.forEach((allocatedMinutes, teacherCode) => {
    const teacher = findTeacher(staff, teacherCode);

    if (teacher) {
      // Determine semester from the lines being processed
      const semester = lines.length > 0 && lines[0].semester ? lines[0].semester as 'semester1' | 'semester2' : 'semester1';
      
      // Calculate allowances with pro-rata calculation based on FTE
      const allowanceMinutes = getTotalAllowanceMinutes(teacher, semester, year);
      
      // Calculate available minutes (FTE × 2220 minutes per semester - pro-rata allowances)
      const availableMinutesPerSemester = (teacher.fte * 2220) - allowanceMinutes;
      
      // Calculate utilization percentage
      const utilization = availableMinutesPerSemester > 0 ? (allocatedMinutes / availableMinutesPerSemester) * 100 : 0;
      
      // Flag as overloaded if over 100% utilization
      if (utilization > 100) {
        const teacherLabel = formatTeacherLabel(teacher, teacherCode);
        const conflict: ConflictData = {
          id: `teacher_overload_${teacherCode}`,
          type: 'teacher_load_exceeded',
          description: `Teacher ${teacherLabel} allocated ${Math.round(utilization)}% of capacity (${allocatedMinutes}/${Math.round(availableMinutesPerSemester)} min)`,
          severity: utilization > 120 ? 'critical' : 'high',
          lineId: 'all',
          lineName: 'All Lines',
          affectedEntities: [{
            type: 'teacher',
            id: teacherCode,
            name: teacherLabel,
            code: teacherCode
          }],
          status: 'open',
          resolutionOptions: [
            'Reassign some classes to other teachers',
            // Advisory (no timetable change): these navigate to the relevant page
            // instead of marking the conflict resolved. See ConflictResolution.tsx.
            'Open Staff Directory to review FTE',
            'Open Allowances to review'
          ],
          details: {
            teacherCode,
            teacherName: teacherLabel,
            utilization: Math.round(utilization),
            allocatedMinutes,
            availableMinutes: Math.round(availableMinutesPerSemester)
          }
        };

        conflicts.push(conflict);
      }
    }
  });

  return conflicts;
}

/**
 * Detect unassigned classes - REMOVED: Not considered conflicts anymore
 */
// function detectUnassignedClasses(grid: StaffAllocationGrid, lines: any[]): ConflictData[] {
//   // This function has been removed as unassigned classes are not conflicts
//   return [];
// }

/**
 * Generate conflict summary statistics
 */
export function generateConflictSummary(conflicts: ConflictData[]): ConflictSummary {
  const totalConflicts = conflicts.length;
  const openConflicts = conflicts.filter(c => c.status === 'open').length;
  const criticalConflicts = conflicts.filter(c => c.severity === 'critical').length;

  const conflictsByType: Record<string, number> = {};
  conflicts.forEach(conflict => {
    conflictsByType[conflict.type] = (conflictsByType[conflict.type] || 0) + 1;
  });

  return {
    totalConflicts,
    openConflicts,
    criticalConflicts,
    conflictsByType
  };
}

/**
 * Get conflict type definitions for display
 */
export function getConflictTypeDefinitions(): Array<{
  id: string;
  name: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
}> {
  return [
    {
      id: 'teacher_double_booking',
      name: 'Teacher Double Allocation',
      description: 'Teacher assigned to multiple classes in the same line',
      severity: 'critical'
    },
    // Removed: Not using rooms in this system
    // {
    //   id: 'room_double_booking',
    //   name: 'Room Double Booking', 
    //   description: 'Multiple classes assigned to the same room at the same time',
    //   severity: 'critical'
    // },
    {
      id: 'teacher_load_exceeded',
      name: 'Teaching Load Exceeded',
      description: 'Teacher assigned more classes than their maximum load',
      severity: 'high'
    },
    // Removed: Unassigned classes are not conflicts, just incomplete data
    // {
    //   id: 'unassigned_class',
    //   name: 'Unassigned Classes',
    //   description: 'Classes that have no teacher assigned',
    //   severity: 'medium'
    // }
  ];
}